import { HOST, API } from '@/utils/http'
import { v4 as uuidv4 } from 'uuid'
import { centroid } from '@turf/turf'
import { toast } from "@/components/ui/toast/use-toast"
import { getResources, uploadResource, deleteResource } from '@/services/resourcesApi'

const featureToCoordinates = (feature) => {
  const geometry = feature?.geometry
  if (geometry?.type === 'Point' && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates
  }
  const props = feature?.properties || {}
  if (props.center_lng != null && props.center_lat != null) {
    return [props.center_lng, props.center_lat]
  }
  if (geometry) {
    try {
      return centroid(geometry).geometry.coordinates
    } catch (e) {
      return null
    }
  }
  return null
}

// Transient geometry rendered for an externally-opened feature (e.g. a daily-review
// report link), mirroring the MapSearch preview pattern. Independent of project layers.
const TRANSIENT_SOURCE = 'geo-popup-feature'

const removeTransientGeometry = (map) => {
  if (!map) return
  if (map.getLayer(`${TRANSIENT_SOURCE}-fill`)) map.removeLayer(`${TRANSIENT_SOURCE}-fill`)
  if (map.getLayer(`${TRANSIENT_SOURCE}-line`)) map.removeLayer(`${TRANSIENT_SOURCE}-line`)
  if (map.getLayer(`${TRANSIENT_SOURCE}-circle`)) map.removeLayer(`${TRANSIENT_SOURCE}-circle`)
  if (map.getSource(TRANSIENT_SOURCE)) map.removeSource(TRANSIENT_SOURCE)
}

const addTransientGeometry = (map, feature) => {
  if (!map || !feature?.geometry) return
  try {
    removeTransientGeometry(map)
    map.addSource(TRANSIENT_SOURCE, { type: 'geojson', data: feature })
    const type = feature.geometry.type
    const color = ['coalesce', ['get', 'color'], '#0059ff']
    if (type === 'Polygon' || type === 'MultiPolygon') {
      map.addLayer({ id: `${TRANSIENT_SOURCE}-fill`, type: 'fill', source: TRANSIENT_SOURCE, paint: { 'fill-color': color, 'fill-opacity': 0.5 } })
      map.addLayer({ id: `${TRANSIENT_SOURCE}-line`, type: 'line', source: TRANSIENT_SOURCE, paint: { 'line-color': color, 'line-width': 2, 'line-opacity': 0.9 } })
    } else if (type === 'LineString' || type === 'MultiLineString') {
      map.addLayer({ id: `${TRANSIENT_SOURCE}-line`, type: 'line', source: TRANSIENT_SOURCE, paint: { 'line-color': color, 'line-width': 3, 'line-opacity': 0.9 } })
    } else {
      map.addLayer({ id: `${TRANSIENT_SOURCE}-circle`, type: 'circle', source: TRANSIENT_SOURCE, paint: { 'circle-color': color, 'circle-radius': 8, 'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff' } })
    }
  } catch (err) {
    const msg = err?.message ?? ''
    const transient = msg.includes('Style is not done loading') || msg.includes('Source') || msg.includes('style')
    if (!transient) throw err
    // Style still processing (e.g. link clicked mid style-load/reload) — retry on next styledata tick
    map.once('styledata', () => addTransientGeometry(map, feature))
  }
}

// Fetch a feature by code and open its popup on the persistent map (used by
// daily-review report links). Independent of which project layer is mounted.
export const openFeaturePopupByCode = async ({ commit, getters }, code) => {
  if (!code) return
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/geo/feature/${code}`
    })
    const feature = response.data
    const coordinates = featureToCoordinates(feature)

    if (!coordinates) {
      toast({ title: "Can't locate this report on the map", variant: 'destructive' })
      return
    }

    const map = getters.getMap
    if (map) {
      addTransientGeometry(map, feature)
      const zoom = Math.max(map.getZoom?.() || 0, 14)
      map.flyTo({ center: coordinates, zoom, duration: 800 })
    }

    commit('SET_FEATURE_POPUP', { feature, coordinates })
  } catch (e) {
    toast({ title: "Can't open report on the map", variant: 'destructive' })
  }
}

// Close the externally-opened feature popup and remove its transient geometry.
export const closeFeaturePopup = ({ commit, getters }) => {
  removeTransientGeometry(getters.getMap)
  commit('CLEAR_FEATURE_POPUP')
}


export const fetchBeacons = async ({commit}, payload) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/geo/beacons/${payload.project_id}`
    })

    for (const beacon of response.data) {
      commit('NEXT_BEACON', beacon)
    }
  } catch (e) {
    // commit('SET_ALERT', {type: 'error', text: `Can't fetch beacons`}, {root: true})
  }
}


export const fetchMapFeatures = async ({commit}, payload) => {
  try {
    const { org_id, ...params } = payload
    const response = await API({
      method: 'GET',
      url: `${HOST}/organization/${org_id}/features`,
      params
    })
    commit('MERGE_FEATURES', {
      features: response.data,
    })
    commit('SET_TOTAL_ONMAP', response.data?.total ?? response.data?.features?.length ?? 0)
  } catch (e) {
    if (!e.response) {
      toast({ title: "Can't fetch features", variant: 'destructive' })
      return
    }

    if (e.response.status === 403) {
      toast({ title: "You don't have permission to access this project", variant: 'destructive' })
    } else {
      throw new Error(e)
    }
  }
}

export const fetchFeature = async ({commit}, code) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/geo/feature/${code}`
    })

    const feature = response.data

    commit('SET_CURRENT_FEATURE', {feature: feature})
  } catch (e) {
    if (!e.response) {
      toast({ title: "Can't fetch report", variant: 'destructive' })
      return
    }

    if (e.response.status === 403) {
      toast({ title: "You don't have permission to access this report", variant: 'destructive' })
    }

    if (e.response.status === 404) {
      toast({ title: "Report not found", variant: 'destructive' })
    }
  }
}

export const fetchResources = async ({commit}, featureId) => {
  try {
    const response = await getResources(featureId)
    commit('SET_RESOURCES', response.resources || [])
    return response.resources
  } catch (error) {
    console.error('Failed to fetch resources:', error)
    toast({
      title: 'Failed to fetch resources',
      description: error.message,
      variant: 'destructive'
    })
    throw error
  }
}

export const uploadResourceNew = async ({commit}, payload) => {
  try {
    const response = await uploadResource(
      payload.file,
      payload.featureCode,
      payload.onProgress
    )
    // Add all resource variants to the store
    response.resources.forEach(resource => {
      commit('ADD_RESOURCE', resource)
    })
    return response
  } catch (error) {
    console.error('Failed to upload resource:', error)
    toast({
      title: 'Upload failed',
      description: error.message,
      variant: 'destructive'
    })
    throw error
  }
}

export const deleteResourceAction = async ({commit}, resourceId) => {
  try {
    await deleteResource(resourceId)
    commit('REMOVE_RESOURCE', resourceId)
    toast({
      title: 'Resource deleted',
      description: 'Resource has been successfully deleted'
    })
  } catch (error) {
    console.error('Failed to delete resource:', error)
    toast({
      title: 'Delete failed',
      description: error.message,
      variant: 'destructive'
    })
    throw error
  }
}

export const loadHeavyFeatureGeometry = async ({commit, state}, code) => {
  try {
    commit('SET_HEAVY_FEATURE_LOADING', { code, loading: true })

    const response = await API({
      method: 'GET',
      url: `${HOST}/geo/feature/${code}`
    })

    const geometry = response.data?.geometry
    if (geometry) {
      commit('SET_HEAVY_FEATURE_GEOMETRY', { code, geometry })
    }

    commit('SET_HEAVY_FEATURE_LOADING', { code, loading: false })
  } catch (e) {
    commit('SET_HEAVY_FEATURE_LOADING', { code, loading: false })
    toast({ title: "Can't load feature geometry", variant: 'destructive' })
  }
}

export const saveResource = async ({commit}, payload) => {
  try {
    const response = await API({
      headers: {'content-type': 'multipart/form-data'},
      method: 'POST',
      url: `${HOST}/geo/feature/${payload.code}/resource`,
      params: {
        uuid: payload.uuid
      },
      data: {
        file: payload.file
      }
    })
  } catch (e) {
    throw new Error(e)
  }
}

export const saveFeature = async ({commit, dispatch, state}, request) => {
  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/geo/feature?project_id=${request.project_id}${request.monitor_area ? '&monitor_area=true' : ''}`,
      data: request.feature
    })

    const feature = response.data
    // Set when the feature was saved but its "Monitor area" switch could not
    // be honored (project monitoring off, cap reached, ...).
    feature.monitoringWarning = response.headers['x-monitoring-warning'] || null
    const projectStr = `project-${request.project_id}-`

    if (state.map.getSource(`${projectStr}reports_points`)) {
      if (feature.geometry.type === 'Point') {
        state.map.getSource(`${projectStr}reports_points`).setData(feature)
      } else {
        state.map.getSource(`${projectStr}reports_mixed`).setData(feature)
      }
    }

    if (request.files) {
      for (const file of request.files) {
        await dispatch('saveResource', {
          code: feature.properties.code,
          file: file,
          uuid: uuidv4()
        })
      }
    }

    return feature
  } catch (e) {
    throw new Error(e)
  }
}

export const updateFeature = async ({commit, state}, payload) => {
  try {
    const response = await API({
      method: 'PUT',
      url: `${HOST}/geo/feature/${payload.feature.properties.code}`,
      data: payload.feature
    })

    const feature = response.data
    const projectStr = `project-${payload.project_id}-`

    if (state.map.getSource(`${projectStr}reports_points`)) {
      if (feature.geometry.type === 'Point') {
        state.map.getSource(`${projectStr}reports_points`).setData(feature)
      } else {
        state.map.getSource(`${projectStr}reports_mixed`).setData(feature)
      }
    }

    commit('SET_CURRENT_FEATURE', {feature: feature})
    return feature
  } catch (e) {
    throw new Error(e)
  }
}

export const pairToProject = async ({commit}, payload) => {
  try {
    const response = await API({
      method: 'PUT',
      url: `${HOST}/geo/feature/${payload.properties.code}/pair`
    })
  } catch (e) {
    throw new Error(e)
  }
}

export const updateFeatureGeometry = async ({commit}, feature) => {
  try {
    const response = await API({
      method: 'PUT',
      url: `${HOST}/geo/feature/${feature.properties.code}/geometry`,
      data: feature.geometry
    })
  } catch (e) {
    throw new Error(e)
  }
}


export const deleteFeature = async ({ commit }, code) => {
  try {
    await API({
      method: 'DELETE',
      url: `${HOST}/geo/feature/${code}`
    })

    commit('DELETE_FEATURE', code)
  } catch(e) {
    throw new Error(e)
  }
}

// Beacons
export const fetchLatestBeacons = async ({commit}, projectId) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/geo/beacons/${projectId}`
    })

    for (const beacon of response.data) {
      commit('NEXT_BEACON', beacon)
    }
  } catch (e) {
    throw new Error(e)
  }
}

export const mapReady = async ({commit}, payload) => {
  commit('SET_MAP_READY', true)
}

// Report Templates

export const generateReportTemplate = async (_ctx, { projectId, prompt, generate_fields, generate_summary_prompt }) => {
  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/project/${projectId}/templates/generate`,
      data: { prompt, generate_fields, generate_summary_prompt }
    })
    return response.data
  } catch (e) {
    const detail = e?.response?.data?.detail
    toast({ title: detail || "Can't generate template", variant: 'destructive' })
    throw e
  }
}

export const createReportTemplate = async ({commit}, payload) => {
  try {
    const { template, icon } = payload

    // Use admin endpoint if project_id is undefined, otherwise use project endpoint
    const isAdminContext = template.project_id === undefined
    const createUrl = isAdminContext
      ? `${HOST}/admin/templates`
      : `${HOST}/project/${template.project_id}/templates`

    const response = await API({
      method: 'POST',
      url: createUrl,
      data: template
    })

    const newTemplate = response.data

    if (icon) {
      const formData = new FormData()
      formData.append('icon', icon)

      // Use admin endpoint for icon upload if in admin context
      const iconUrl = isAdminContext
        ? `${HOST}/admin/templates/${newTemplate.id}/upload-icon`
        : `${HOST}/project/${template.project_id}/templates/${newTemplate.id}/upload-icon`

      const response = await API({
        method: 'PUT',
        url: iconUrl,
        data: formData
      })

      commit('ADD_REPORT_TEMPLATE', response.data)
    } else {
      commit('ADD_REPORT_TEMPLATE', newTemplate)
    }

  } catch (e) {
    toast({ title: "Can't create report template", variant: 'destructive' })
  }
}

// headers: {'content-type': 'multipart/form-data'},


export const updateReportTemplate = async ({commit}, payload) => {
  try {
    const { project_id, template, icon } = payload

    // Use admin endpoint if project_id is undefined, otherwise use project endpoint
    const isAdminContext = project_id === undefined
    const updateUrl = isAdminContext
      ? `${HOST}/admin/templates/${template.id}`
      : `${HOST}/project/${project_id}/templates/${template.id}`

    const response = await API({
      method: 'PUT',
      url: updateUrl,
      data: template
    })

    const newTemplate = response.data

    if (icon) {
      const formData = new FormData()
      formData.append('icon', icon)

      // Use admin endpoint for icon upload if in admin context
      const iconUrl = isAdminContext
        ? `${HOST}/admin/templates/${newTemplate.id}/upload-icon`
        : `${HOST}/project/${project_id}/templates/${newTemplate.id}/upload-icon`

      const response = await API({
        method: 'PUT',
        url: iconUrl,
        data: formData
      })

      commit('UPDATE_REPORT_TEMPLATE', response.data)
    } else {
      commit('UPDATE_REPORT_TEMPLATE', newTemplate)
    }
  } catch (e) {
    toast({ title: "Can't update report template", variant: 'destructive' })
  }
}

export const fetchProjectReportTemplates = async ({commit}, projectId) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/project/${projectId}/templates`
    })

    commit('SET_REPORT_TEMPLATES', response.data)
  } catch (e) {
    toast({ title: "Can't fetch report templates", variant: 'destructive' })
  }
}

export const fetchAllTemplates = async ({commit}, organizationId = null) => {
  try {
    if (!organizationId) {
      toast({ title: "No organization selected", variant: 'destructive' })
      return
    }

    const response = await API({
      method: 'GET',
      url: `${HOST}/organization/${organizationId}/templates`
    })

    commit('SET_ALL_TEMPLATES', response.data)
  } catch (e) {
    toast({ title: "Can't fetch templates", variant: 'destructive' })
  }
}


export const deleteReportTemplate = async ({commit}, payload) => {
  try {
    const { project_id, template } = payload

    // Use admin endpoint if project_id is undefined, otherwise use project endpoint
    const isAdminContext = project_id === undefined
    const deleteUrl = isAdminContext
      ? `${HOST}/admin/templates/${template.id}`
      : `${HOST}/project/${project_id}/templates/${template.id}`

    await API({
      method: 'DELETE',
      url: deleteUrl
    })

    commit('DELETE_REPORT_TEMPLATE', template)
  } catch (e) {
    toast({ title: "Can't delete report template", variant: 'destructive' })
  }
}

// Templates Library Actions

export const fetchSystemTemplatesLibrary = async ({commit}, projectId) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/project/${projectId}/templates/library`
    })

    commit('SET_SYSTEM_TEMPLATES_LIBRARY', response.data)
  } catch (e) {
    toast({ title: "Can't fetch templates library", variant: 'destructive' })
  }
}

export const attachTemplateToProject = async ({commit, dispatch}, {projectId, template}) => {
  try {
    await API({
      method: 'POST',
      url: `${HOST}/project/${projectId}/templates/${template.id}/attach`
    })

    // Remove from library and add to project templates
    commit('REMOVE_FROM_LIBRARY', template.id)
    commit('ADD_REPORT_TEMPLATE', template)

    toast({ title: 'Template added to project successfully' })
    return true
  } catch (e) {
    const errorMessage = e.response?.data?.detail || "Can't attach template to project"
    toast({ title: errorMessage, variant: 'destructive' })
    return false
  }
}

export const unattachTemplateFromProject = async ({commit}, {projectId, template}) => {
  try {
    await API({
      method: 'DELETE',
      url: `${HOST}/project/${projectId}/templates/${template.id}/unattach`
    })

    // Remove from project templates
    commit('DELETE_REPORT_TEMPLATE', template)

    // If it's a system template, add it back to the library
    if (template.is_system_template) {
      commit('ADD_TO_LIBRARY', template)
    }

    toast({ title: 'Template removed from project successfully' })
    return true
  } catch (e) {
    const errorMessage = e.response?.data?.detail || "Can't remove template from project"
    toast({ title: errorMessage, variant: 'destructive' })
    return false
  }
}

